import { useCallback, useState } from 'react';
import { IconButton, Snackbar, Tooltip } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import type { EmbedShareInfo } from '@/types/embed.types';

interface ShareLinkButtonProps {
  getInfo: () => Promise<EmbedShareInfo>;
  label?: string;
}

function fallbackCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

export function ShareLinkButton({ getInfo, label = 'Copy share link' }: ShareLinkButtonProps) {
  const [showCheck, setShowCheck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleCopy = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const info = await getInfo();

      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(info.url);
          copied = true;
        } catch {
          copied = fallbackCopy(info.url);
        }
      } else {
        copied = fallbackCopy(info.url);
      }

      if (copied) {
        setShowCheck(true);
        setToastMessage('Share link copied.');
        setTimeout(() => setShowCheck(false), 900);
      } else {
        setToastMessage("Couldn't copy. Press Cmd/Ctrl+C to copy manually.");
      }
    } catch (err) {
      setToastMessage(`Couldn't prepare share link: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [busy, getInfo]);

  return (
    <>
      <Tooltip title={showCheck ? 'Copied' : label} placement="bottom">
        <IconButton
          onClick={() => void handleCopy()}
          disabled={busy}
          aria-label={label}
          sx={{
            width: 44,
            height: 44,
            color: showCheck ? '#5EBA7D' : 'rgba(255,255,255,0.65)',
            transition: 'color 200ms ease-out',
            '&:hover': { color: showCheck ? '#5EBA7D' : 'rgba(255,255,255,0.95)' },
          }}
        >
          {showCheck ? <CheckRoundedIcon /> : <IosShareRoundedIcon />}
        </IconButton>
      </Tooltip>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={2200}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: '#2A2C30',
            color: 'rgba(255,255,255,0.95)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            fontSize: 14,
            minWidth: 0,
          },
        }}
      />
    </>
  );
}

export default ShareLinkButton;
